import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Tabs,
  Tab,
  Badge,
  CircularProgress
} from '@mui/material';
import { useCV } from '../contexts/CVContext';
import SkillsItems from './common/SkillsItems';
import { radarApi } from '../../../../services/api';

const Skills = () => {
  const { isDataReady, cvData, updateSkills, readonly } = useCV();
  
  // State for segments/skill categories
  const [skillCategories, setSkillCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  
  // Set up state for the skill skills
  const [skills, setSkills] = useState(cvData.skills || []);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedIndices, setExpandedIndices] = useState({});
  
  // Get skill categories from API (includes segmentProcesses for controlling competency assignment)
  useEffect(() => {
    const fetchSkillCategories = async () => {
      try {
        setIsLoadingCategories(true);

        const response = await radarApi.getFirstLevelHierarchy();
        if (response && Array.isArray(response)) {
          setSkillCategories(response);

          // Initialize expandedIndices based on fetched categories
          const initialExpandedIndices = {};
          response.forEach(category => {
            initialExpandedIndices[category.id] = null;
          });
          setExpandedIndices(initialExpandedIndices);
        } else {
          console.error('Error fetching skill categories');
        }
      } catch (error) {
        console.error('Error fetching skill categories:', error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    // Only load categories when data is ready
    if (isDataReady) {
      fetchSkillCategories();
    }
  }, [isDataReady]);
  
  // Update local state when context data changes, but only on initial load
  useEffect(() => {
    if (cvData.skills?.length > 0 && isDataReady) {
      setSkills(cvData.skills);
    }
  }, [cvData.skills, isDataReady]);
  
  // Function to handle tab changes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Reset expanded indices when changing tabs
    if (skillCategories.length > 0) {
      const currentCategory = skillCategories[activeTab];
      if (currentCategory) {
        setExpandedIndices({
          ...expandedIndices,
          [currentCategory.id]: null
        });
      }
    }
  };
  
  // Get the current category
  const currentCategory = skillCategories[activeTab];
  
  // Find the skills for the current category - now grouped by firstLevelSegmentId
  const categorySkills = currentCategory ? 
    skills.filter(skill => skill.firstLevelSegmentId === currentCategory.id) || [] : [];
  
  // Custom handler to update both local state and context
  const handleSkillsChange = (newSkills) => {
    if (!currentCategory) return;
    
    // Create a copy of the skills array
    let updatedSkills = [...skills];

    // Remove all skills that belong to the current category
    updatedSkills = updatedSkills.filter(skill => skill.firstLevelSegmentId !== currentCategory.id);
    
    // Add the new skills with their order property and firstLevelSegmentId
    const newSkillsWithOrder = newSkills.map((skill, index) => ({
      ...skill,
      order: index + 1,
      firstLevelSegmentId: currentCategory.id,
      categoryName: currentCategory.name,
      // For custom skills, ensure they have the firstLevelSegmentId
      ...(skill.belongsToRadar === false ? { 
        firstLevelSegmentId: currentCategory.id,
        categoryName: currentCategory.name
      } : {})
    }));
    
    // Add new skills to the array
    updatedSkills = [...updatedSkills, ...newSkillsWithOrder];
    
    // Update state and context
    setSkills(updatedSkills);
    updateSkills(updatedSkills);
  };

  // Show loading state while fetching categories
  if (isLoadingCategories) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading skill categories...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', overflow: 'visible' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="skill categories tabs"
        >
          {skillCategories
            .sort((a, b) => a.order - b.order)
            .map((category, index) => {
              // Count skills in this category by firstLevelSegmentId
              const itemCount = skills.filter(skill => skill.firstLevelSegmentId === category.id).length;
              
              return (
                <Tab 
                  key={category.id} 
                  id={`skill-tab-${index}`}
                  label={
                    <Badge 
                      badgeContent={itemCount} 
                      color="primary"
                      size="small"
                      sx={{
                        '& .MuiBadge-badge': {
                          right: -7,
                          top: 0,
                          fontSize: '0.65rem',
                          height: '16px',
                          minWidth: '16px',
                          padding: '0 4px'
                        }
                      }}
                    >
                      {category.name}
                    </Badge>
                  }
                />
              );
            })}
        </Tabs>
      </Box>
      
      {/* Remove the loading indicator for category description, as it will be moved to SkillsItems */}
      
      {skillCategories.map((category, index) => (
        <Box
          key={category.id}
          role="tabpanel"
          hidden={activeTab !== index}
          id={`skill-tabpanel-${index}`}
          aria-labelledby={`skill-tab-${index}`}
          sx={{ position: 'relative', overflow: 'visible' }}
        >
          {activeTab === index && (
            <SkillsItems
              skills={categorySkills}
              onSkillsChange={handleSkillsChange}
              expandedIndex={expandedIndices[category.id]}
              setExpandedIndex={(index) =>
                setExpandedIndices({
                  ...expandedIndices,
                  [category.id]: index
                })
              }
              //subcategory={currentSubcategory}
              subcategoryId={currentCategory ? currentCategory.id : null}
              firstLevelSegment={currentCategory}
              radarLoading={false}
              addButtonText={categorySkills.length > 0 ? `Add one more ${category.name}` : `Add a ${category.name}`}
              emptyStateTitle={`You haven't added any ${category.name.toLowerCase()} skills yet.`}
              emptyStateDescription={`Add skills related to ${category.name.toLowerCase()}.`}
              readonly={readonly}
              showRadarNavigation={false}
              showRadarViewToggle={false}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default Skills; 